import React, { useEffect, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'

import { Modal, Button, FieldLabel, fieldFill, fieldBorderColor } from '@repo/ui'
import { CodeInput } from 'components/input'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

import {
  useConfirmTotpEnrollmentMutation,
  type StartTotpEnrollmentResponse,
} from 'hooks/api/useAuthFactors'

interface EnrollTotpModalProps {
  open: boolean
  onClose: () => void
  enrollment: StartTotpEnrollmentResponse | null
  onConfirmed: (recoveryCodes?: string[]) => void
}

const CODE_LENGTH = 6

/**
 * VRG-490 — the QR + manual-entry secret are only ever returned by the
 * START call, once (see `useStartTotpEnrollmentMutation`), so `enrollment`
 * is seeded by the caller BEFORE this opens rather than fetched here.
 */
export const EnrollTotpModal = ({ open, onClose, enrollment, onConfirmed }: EnrollTotpModalProps) => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { confirmTotpEnrollmentAsync, isConfirmingTotpEnrollment } = useConfirmTotpEnrollmentMutation()

  useEffect(() => {
    if (open) {
      setCode('')
      setError(null)
    }
  }, [open])

  // `submitted` comes from CodeInput's `onComplete`, which fires inside the same
  // event that sets the code — so state is one render behind and would submit five
  // digits. The button path passes nothing and reads state as before.
  const handleConfirm = async (submitted?: string) => {
    const value = (submitted ?? code).trim()
    if (value.length !== CODE_LENGTH || isConfirmingTotpEnrollment) return
    setError(null)
    try {
      const response = await confirmTotpEnrollmentAsync({ code: value })
      notification({ type: 'success', title: 'Authenticator app added', text: 'You can now use it to sign in.' })
      onConfirmed(response.recoveryCodes)
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  return (
    <Modal
      title="Add authenticator app"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={onClose} disabled={isConfirmingTotpEnrollment}>
            Cancel
          </Button>
          <Button
            disableElevation
            variant="primary"
            loading={isConfirmingTotpEnrollment}
            disabled={code.trim().length !== CODE_LENGTH || isConfirmingTotpEnrollment}
            // Wrapped — `handleConfirm` takes an optional code, so passing it by
            // reference would submit the MouseEvent as the code.
            onClick={() => handleConfirm()}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Scan this code with an authenticator app (1Password, Authy, Google Authenticator), or enter the key
          manually. Then type the 6-digit code it shows.
        </Typography>

        {enrollment && (
          <Stack alignItems="center" spacing={3}>
            {/* VRG-490 round 2 — square, no radius, and a recessed fill rather
                than a bordered card (`fieldFill`/`fieldBorderColor`, the same
                "one step darker than the surface" token every form field
                already uses — §31). Padding around the image is what lets the
                fill actually show; the QR itself stays flush white so it keeps
                scanning correctly. */}
            <Box
              sx={{
                p: 3,
                borderRadius: 0,
                backgroundColor: fieldFill(theme),
                border: `1px solid ${fieldBorderColor(theme)}`,
              }}
            >
              <Box
                component="img"
                src={enrollment.qrCodeDataUri}
                alt="Scan this QR code with your authenticator app"
                sx={{ width: 180, height: 180, display: 'block' }}
              />
            </Box>
            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="body4" color="text.secondary">
                Can't scan? Enter this key manually
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  px: 3,
                  py: 2,
                  borderRadius: 0,
                  backgroundColor: fieldFill(theme),
                  border: `1px solid ${fieldBorderColor(theme)}`,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body3" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {enrollment.secret}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}

        <Stack spacing={2}>
          {/* VRG-610 — the same per-digit boxes the SIGN-IN challenge uses. This was
              a single text input, so entering an authenticator code looked like two
              different controls depending on whether you were enrolling or signing
              in, for identical input. `onComplete` also submits on the sixth digit,
              matching the challenge. */}
          <FieldLabel label="6-digit code" />
          <CodeInput
            autoFocus
            value={code}
            onChange={value => {
              setCode(value)
              setError(null)
            }}
            onComplete={value => handleConfirm(value)}
            disabled={isConfirmingTotpEnrollment}
            length={CODE_LENGTH}
            aria-label="Authenticator code"
          />
          {error && (
            <Typography variant="body3" color="error.main">
              {error}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Modal>
  )
}
